import React from 'react';
/** Co-located in practice/ domain (CL-1 s64). */
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
export interface AccordionSection {
  id: string;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  content: React.ReactNode;
  pulse?: boolean;
}
interface FeedbackAccordionProps {
  sections: AccordionSection[];
  expandedSection: string | null;
  onToggle: (id: string | null) => void;
}
export function FeedbackAccordion({
  sections,
  expandedSection,
  onToggle
}: FeedbackAccordionProps) {
  if (!sections || sections.length === 0) return null;
  return <div className="w-full space-y-2.5" data-id="element-7539">
      {sections.map((section, index) => {
      const isExpanded = expandedSection === section.id;
      return <motion.div key={section.id} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: index * 0.05,
        duration: 0.3
      }} className="relative bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-7540">
            {section.pulse && !isExpanded && <motion.div className="absolute inset-0 rounded-2xl pointer-events-none" animate={{
          boxShadow: ['0 0 0 0 rgba(249, 115, 22, 0)', '0 0 0 4px rgba(249, 115, 22, 0.12)', '0 0 0 0 rgba(249, 115, 22, 0)']
        }} transition={{
          duration: 2,
          repeat: 2,
          repeatDelay: 0.5
        }} data-id="element-7541" />}

            <button onClick={() => onToggle(isExpanded ? null : section.id)} onKeyDown={e => {
          if (e.key === 'Escape' && isExpanded) {
            e.stopPropagation();
            onToggle(null);
          }
        }} className={`w-full flex items-center justify-between p-3.5 text-left transition-colors ${isExpanded ? 'bg-slate-50/50' : 'hover:bg-slate-50/50'}`} aria-expanded={isExpanded} aria-controls={`${section.id}-content`} id={`${section.id}-header`} data-id="element-7542">
              <div className="flex items-center gap-3" data-id="element-7543">
                <div className={`flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-xl ${section.id === 'explanation' ? 'bg-amber-100 text-amber-600' : section.id === 'tip' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-600'}`} data-id="element-7544">
                  {section.icon}
                </div>
                <div data-id="element-7545">
                  <h3 className="font-bold text-slate-800 text-sm leading-tight" data-id="element-7546">
                    {section.title}
                  </h3>
                  {section.subtitle && !isExpanded && <p className="text-[11px] font-medium text-slate-500 mt-0.5" data-id="element-7547">
                      {section.subtitle}
                    </p>}
                </div>
              </div>
              <div className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-colors ${isExpanded ? 'bg-slate-200 text-slate-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-7548">
                <motion.div animate={{
              rotate: isExpanded ? 180 : 0
            }} transition={{
              duration: 0.25,
              ease: 'easeInOut'
            }} data-id="element-7549">
                  <ChevronDown className="w-4 h-4" data-id="element-7550" />
                </motion.div>
              </div>
            </button>

            <AnimatePresence initial={false} data-id="element-7551">
              {isExpanded && <motion.div id={`${section.id}-content`} role="region" aria-labelledby={`${section.id}-header`} initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            height: {
              type: 'spring',
              stiffness: 350,
              damping: 35
            },
            opacity: {
              duration: 0.2
            }
          }} data-id="element-7552">
                  <div className={`px-4 pb-4 pt-1 border-t ${section.pulse ? 'border-rose-100 bg-rose-50/30' : 'border-slate-100 bg-slate-50/30'}`} data-id="element-7553">
                    {section.content}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>;
    })}
    </div>;
}